"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, AnimatePresence } from "motion/react";
import { Check, X, AlertTriangle } from "lucide-react";
import { cn } from "../lib/cn";
import { Spinner } from "../primitives/Spinner";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { springs, transitions } from "../lib/motion";
import {
  useDebouncedAvailabilityCheck,
  type AvailabilityStatus,
  type AvailabilityResult,
} from "../hooks/useDebouncedAvailabilityCheck";

/**
 * AvailabilityInput — a debounced slug / name field with a live status glyph
 * and one-tap "apply this suggestion". The consolidation of the studio
 * custom-URL field, the workspace-name field, and the account-username field,
 * which each hand-rolled the same debounce, status state machine, suggestion
 * wiring, and (in two of three copies) off-brand hex.
 *
 * It is a thin shell over the brand `Input` primitive plus
 * `useDebouncedAvailabilityCheck` (the debounce + AbortController/request-id
 * out-of-order guard + tri-state machine). The trailing glyph morphs
 * idle -> spinner (checking) -> check (available) -> cross (taken) ->
 * warn (error) through an `AnimatePresence` keyed on status; under
 * `prefers-reduced-motion` the swap and the suggestion entrance become instant
 * opacity toggles (no spring, no rise).
 *
 * What lives here vs. what stays in the consumer:
 *   - The field owns: the framed input (optional `prefix` / `suffix`
 *     adornments such as `.aisquare.studio`), the focus ring, the status glyph,
 *     the `aria-live` status row, and the suggestion-apply UI.
 *   - The consumer owns: the transport (`checkAvailability`, injected, never
 *     service-imported), any client-side sanitization (`sanitize`) and sync
 *     validation (`validate`), and persistence.
 *
 * `checkAvailability` matches the live `StudioUrlCheck` return shape
 * (`{ available, suggested? }`); pass `getAvailability` to adapt a different
 * response (e.g. the workspace `{ available, message }`).
 *
 * @example
 *   <AvailabilityInput
 *     aria-label="Custom URL"
 *     value={slug}
 *     onChange={setSlug}
 *     suffix=".aisquare.studio"
 *     checkAvailability={(v) => triggerValidateUrl(v).unwrap()}
 *   />
 */

/** Border + glyph tint per lifecycle phase, layered via `data-status`. */
const availabilityFrameVariants = cva(
  [
    "relative flex w-full items-center gap-1.5 transition-all duration-180 ease-smooth-out",
    "border bg-background ring-offset-background",
    // The focus RING OFFSET lives on the variants that actually draw a ring,
    // not here. When the base owned it, every faceless variant had to REMEMBER
    // to zero it, and three of four forgot: Tailwind emits ring-offset as its
    // own box-shadow, so a transparent field with ring-0 still painted a 2px
    // --background box with no ring inside it. Forgetting to ADD an offset is a
    // flush ring, which is cosmetic; forgetting to ZERO one is a visible box on
    // a field that is meant to be invisible. Putting it on the variants makes
    // the failure mode the harmless one.
    "focus-within:outline-none focus-within:ring-2",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "rounded-lg border-input focus-within:ring-ring focus-within:ring-offset-2",
        search: "rounded-full border-input bg-muted/40 focus-within:ring-ring focus-within:ring-offset-2",
        ghost:
          "rounded-none border-transparent bg-transparent shadow-none focus-within:ring-0 focus-within:ring-offset-0",
      },
      size: {
        sm: "min-h-9 px-2.5 text-xs",
        default: "min-h-10 px-3 text-sm",
        lg: "min-h-12 px-3.5 text-base",
      },
      status: {
        idle: "",
        checking: "",
        available: "border-success/60 focus-within:ring-success",
        taken: "border-destructive/70 focus-within:ring-destructive",
        error: "border-warning/70 focus-within:ring-warning",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      status: "idle",
    },
  },
);

/** How a taken-value suggestion is offered. */
export type SuggestionStyle = "link" | "chips";

/** What a network error reads as for gating purposes. */
export type ErrorTreatAs = "available" | "error";

export interface AvailabilityInputProps
  extends Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      "value" | "onChange" | "size" | "prefix"
    >,
    VariantProps<typeof availabilityFrameVariants> {
  /** Current value (controlled). Omit with `defaultValue` for uncontrolled use. */
  value?: string;
  /** Receives the next value on every keystroke and on suggestion-apply. */
  onChange?: (value: string) => void;
  /**
   * Injected availability transport — required, never a service import. Matches
   * the live `StudioUrlCheck` shape; adapt other shapes with `getAvailability`.
   */
  checkAvailability: (
    value: string,
    signal?: AbortSignal,
  ) => Promise<{ available: boolean; suggested?: string; message?: string }>;
  /** Adapt a non-default response onto `{ available, suggestion?, message? }`. */
  getAvailability?: (res: {
    available: boolean;
    suggested?: string;
    message?: string;
  }) => AvailabilityResult;
  /** Sync sanitizer run on every keystroke before `onChange` (e.g. lowercase, strip spaces). */
  sanitize?: (raw: string) => string;
  /**
   * Sync client rule returning an error string, or null when valid. When it
   * returns non-null the debounce is cancelled and the network is not hit
   * (drop-in for numeric-only / trailing-hyphen / length rejects).
   */
  validate?: (value: string) => string | null;
  /** Normalize the value before compare / fire (e.g. trim edge dashes), forwarded to the hook. */
  transform?: (value: string) => string;
  /** Stillness before firing, in ms. Default 400. */
  delay?: number;
  /** Below this length nothing fires; the field rests at idle. Default 1. */
  minLength?: number;
  /** Soft nudge threshold — values shorter than this draw a "short names are cool" hint. Default 3. */
  minRecommended?: number;
  /** The current persisted value, so re-saving it unchanged never flags "taken". */
  skipWhenEqualTo?: string;
  /** Leading adornment inside the frame (e.g. `https://`). */
  prefix?: React.ReactNode;
  /** Trailing adornment inside the frame (e.g. `.aisquare.studio`). */
  suffix?: React.ReactNode;
  /** Inline-link (default) vs. chip list for suggestions. */
  suggestionStyle?: SuggestionStyle;
  /** Called when the user applies a suggestion (after `onChange` fires with it). */
  onApplySuggestion?: (value: string) => void;
  /** Mirror of fe-react's `onUrlValidationChange` — fires on every status transition. */
  onStatusChange?: (status: AvailabilityStatus) => void;
  /** Whether a flaky check blocks (default "error") or passes ("available"). */
  onErrorTreatAs?: ErrorTreatAs;
  /** Override the wrapper className (not the inner input). */
  wrapperClassName?: string;
  /** className for the status / hint row under the field. */
  hintClassName?: string;
}

const DEFAULT_GET_AVAILABILITY = (res: {
  available: boolean;
  suggested?: string;
  message?: string;
}): AvailabilityResult => ({
  available: res.available,
  suggestion: res.suggested,
  message: res.message,
});

/** The status glyph slot — morphs between phases, keyed on `status`. */
function StatusGlyph({
  status,
  reduced,
}: {
  status: AvailabilityStatus;
  reduced: boolean;
}) {
  // Reduced motion (or unresolved `null`): swap instantly, no spring / scale.
  const enter = reduced ? { opacity: 1 } : { opacity: 1, scale: 1 };
  const from = reduced ? { opacity: 0 } : { opacity: 0, scale: 0.6 };
  const settle = reduced ? { duration: 0 } : undefined;

  let node: React.ReactNode = null;
  if (status === "checking") {
    node = <Spinner size="sm" className="text-muted-foreground" decorative />;
  } else if (status === "available") {
    node = (
      <span className="flex size-5 items-center justify-center rounded-full bg-success/15 text-success">
        <Check className="size-3.5" strokeWidth={2.5} aria-hidden />
      </span>
    );
  } else if (status === "taken") {
    node = (
      <span className="flex size-5 items-center justify-center rounded-full bg-destructive/15 text-destructive">
        <X className="size-3.5" strokeWidth={2.5} aria-hidden />
      </span>
    );
  } else if (status === "error") {
    node = (
      <span className="flex size-5 items-center justify-center rounded-full bg-warning/15 text-warning">
        <AlertTriangle className="size-3.5" strokeWidth={2.5} aria-hidden />
      </span>
    );
  }

  // The available check pops with a light overshoot (rewarding); taken / error
  // settle without overshoot (a stop). Spinner uses the gentle settle.
  const spring =
    status === "available"
      ? springs.bouncy
      : status === "taken" || status === "error"
        ? springs.snappy
        : springs.gentle;

  return (
    <span className="flex size-5 flex-shrink-0 items-center justify-center" aria-hidden>
      <AnimatePresence mode="popLayout" initial={false}>
        {node ? (
          <motion.span
            key={status}
            initial={from}
            animate={enter}
            exit={from}
            transition={settle ?? spring}
            className="flex items-center justify-center"
          >
            {node}
          </motion.span>
        ) : null}
      </AnimatePresence>
    </span>
  );
}

export const AvailabilityInput = React.forwardRef<HTMLInputElement, AvailabilityInputProps>(
  (
    {
      className,
      wrapperClassName,
      hintClassName,
      value: controlledValue,
      defaultValue,
      onChange,
      checkAvailability,
      getAvailability = DEFAULT_GET_AVAILABILITY,
      sanitize,
      validate,
      transform,
      delay = 400,
      minLength = 1,
      minRecommended = 3,
      skipWhenEqualTo,
      prefix,
      suffix,
      suggestionStyle = "link",
      onApplySuggestion,
      onStatusChange,
      onErrorTreatAs = "error",
      variant,
      size,
      id,
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedBy,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const reactId = React.useId();
    const inputId = id ?? `availability-input-${reactId}`;
    const hintId = `${inputId}-hint`;

    // Hybrid controlled / uncontrolled — mirrors how ActionInput / Input behave.
    const isControlled = controlledValue !== undefined;
    const [internal, setInternal] = React.useState(
      typeof defaultValue === "string" ? defaultValue : "",
    );
    const value = isControlled ? controlledValue : internal;

    const reducedRaw = useReducedMotion();
    // `null` (unresolved, SSR) is treated as "reduce" so the first paint never
    // animates-then-snaps — the same discipline the package's other primitives use.
    const reduced = reducedRaw !== false;

    // The sync validation gate. When it returns an error string the network is
    // never hit and the field rests on that local message instead.
    const localError = validate ? validate(value) : null;

    const result = useDebouncedAvailabilityCheck({
      value,
      delay,
      minLength,
      skipWhenEqualTo,
      transform,
      enabled: !disabled && localError === null,
      check: checkAvailability,
      getAvailability,
    });

    // fe-react's "never block on a flaky check": surface the error glyph but
    // report it upward as available so the consumer's submit gate stays open.
    const effectiveStatus: AvailabilityStatus =
      result.status === "error" && onErrorTreatAs === "available"
        ? "available"
        : result.status;

    const onStatusChangeRef = React.useRef(onStatusChange);
    React.useEffect(() => {
      onStatusChangeRef.current = onStatusChange;
    });
    React.useEffect(() => {
      onStatusChangeRef.current?.(effectiveStatus);
    }, [effectiveStatus]);

    const commit = (next: string) => {
      const cleaned = sanitize ? sanitize(next) : next;
      if (!isControlled) setInternal(cleaned);
      onChange?.(cleaned);
    };

    const applySuggestion = (suggested: string) => {
      commit(suggested);
      onApplySuggestion?.(suggested);
    };

    // One source of truth for the hint copy + tone, so the border, the glyph,
    // and the aria-live row stay in lockstep.
    const trimmedLen = (transform ? transform(value) : value).length;
    let hintText: React.ReactNode = null;
    let hintTone = "text-muted-foreground";

    if (localError) {
      hintText = localError;
      hintTone = "text-destructive";
    } else if (result.status === "checking") {
      hintText = "Checking availability…";
      hintTone = "text-muted-foreground";
    } else if (result.status === "available") {
      // A short-but-free value still earns the soft nudge (not a block).
      if (trimmedLen > 0 && trimmedLen < minRecommended) {
        hintText = (
          <>
            Short names are cool. We recommend{" "}
            <strong className="font-semibold">{minRecommended}+ characters</strong>.
          </>
        );
        hintTone = "text-warning";
      } else {
        hintText = result.message ?? "That one is available.";
        hintTone = "text-success";
      }
    } else if (result.status === "taken") {
      hintTone = "text-destructive";
      if (result.suggestion && suggestionStyle === "link") {
        hintText = (
          <>
            {result.message ?? "That one is taken."} Try{" "}
            <button
              type="button"
              onClick={() => applySuggestion(result.suggestion as string)}
              className="font-semibold text-foreground underline decoration-dotted underline-offset-2 transition-colors hover:text-success focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              {result.suggestion}
            </button>
            .
          </>
        );
      } else {
        hintText = result.message ?? "That one is taken.";
      }
    } else if (result.status === "error") {
      hintText =
        onErrorTreatAs === "available"
          ? null
          : "Could not check availability right now.";
      hintTone = "text-warning";
    }

    const suggestions =
      result.status === "taken" && result.suggestion && suggestionStyle === "chips"
        ? [result.suggestion]
        : [];

    const isTaken = result.status === "taken";

    return (
      <div className={cn("w-full", className)}>
        <div
          data-status={effectiveStatus}
          className={cn(
            availabilityFrameVariants({ variant, size, status: result.status }),
            disabled && "pointer-events-none opacity-60",
            wrapperClassName,
          )}
        >
          {prefix ? (
            <span
              className="flex-shrink-0 select-none text-muted-foreground"
              aria-hidden
            >
              {prefix}
            </span>
          ) : null}

          <input
            ref={ref}
            id={inputId}
            value={value}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-invalid={isTaken || (localError !== null) || undefined}
            aria-describedby={cn(hintText ? hintId : undefined, ariaDescribedBy) || undefined}
            data-slot="availability-input"
            onChange={(e) => commit(e.target.value)}
            className={cn(
              "min-w-0 flex-1 bg-transparent text-foreground outline-none border-0",
              "placeholder:text-muted-foreground",
              "disabled:cursor-not-allowed",
              size === "sm" ? "py-1.5" : size === "lg" ? "py-3" : "py-2",
            )}
            {...rest}
          />

          {suffix ? (
            <span
              className="flex-shrink-0 select-none text-muted-foreground"
              aria-hidden
            >
              {suffix}
            </span>
          ) : null}

          <StatusGlyph status={result.status} reduced={reduced} />
        </div>

        {/* Status / hint row — one aria-live region so the border tint, the
            glyph, and the announced text always describe the same state. */}
        <div
          id={hintId}
          role="status"
          aria-live="polite"
          className={cn(
            "mt-1.5 min-h-[1.25rem] text-xs",
            hintTone,
            hintClassName,
          )}
        >
          <AnimatePresence mode="wait" initial={false}>
            {hintText ? (
              <motion.span
                key={`${result.status}-${typeof hintText === "string" ? hintText : "node"}`}
                initial={reduced ? { opacity: 0 } : { opacity: 0, y: -2 }}
                animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
                exit={reduced ? { opacity: 0 } : { opacity: 0, y: 2 }}
                transition={reduced ? { duration: 0 } : transitions.hover}
                className="block"
              >
                {hintText}
              </motion.span>
            ) : null}
          </AnimatePresence>
        </div>

        {/* Suggestion chips (fe-react style). Fade-and-rise on mount, staggered. */}
        {suggestions.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {suggestions.map((s, i) => (
              <motion.button
                key={s}
                type="button"
                onClick={() => applySuggestion(s)}
                initial={reduced ? { opacity: 0 } : { opacity: 0, y: 4 }}
                animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
                transition={
                  reduced ? { duration: 0 } : { ...transitions.surface, delay: i * 0.04 }
                }
                className="inline-flex items-center rounded-full border border-input bg-background px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-success hover:text-success focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                {s}
              </motion.button>
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);
AvailabilityInput.displayName = "AvailabilityInput";
